import {ReactNode, ReactElement, ButtonHTMLAttributes} from 'react';

import styled from 'styled-components';

import {ActionStyle, ActionType, BodyText, BodyTypes, ButtonStyle, ButtonWidth, H3} from 'components/design-guide';
import Action from 'components/design-guide/Action';
import ActionWrapper from 'components/design-guide/Action/ActionWrapper';
import Modal from 'shared/components/Modals/Modal';
import FormattedMessage from 'shared/IntlFormatMessage/components/FormattedMessage';
import {GLOBAL_TRANSLATIONS} from 'shared/IntlFormatMessage/translations/globalTranslations';
import {Spacing} from 'styles/Base';

/**
 * @prop isOpen - Whether or not the prompt modal is open.
 * @prop name - Name override of the modal for analytics
 * @prop title - Prompt headline.
 * @prop message - Prompt message body.
 * @prop confirmationButton - Confirmation button to use.
 * @prop onCancel - Cancel button click handler.
 */
interface Props {
  isOpen: boolean;
  confirmationButton: ReactElement<ButtonHTMLAttributes<{}>>;
  name?: string;
  title?: ReactNode;
  overrideDefaultTitleFormatting?: boolean;
  message?: ReactNode;
  children?: ReactNode;
  cancelCaption?: string | ReactNode;
  onCancel?: () => void;
}

const ConfirmationPrompt = ({
  isOpen,
  name,
  title,
  message,
  children,
  confirmationButton,
  cancelCaption,
  onCancel,
  overrideDefaultTitleFormatting = false
}: Props) => {
  /**
   * Invoke the onCancel prop if it exists.
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      name={name || 'confirmation-modal'}
      aria={{labelledby: 'confirmation-prompt-title'}}
      {...{isOpen}}
      onRequestClose={handleCancel}
      style={{
        content: {
          padding: '1.5rem',
          top: '-12%'
        }
      }}>
      <PromptContent>
        <div id="confirmation-prompt-title">
          {!!title &&
            (overrideDefaultTitleFormatting ? (
              title
            ) : (
              <H3 margin={`${Spacing.ZERO} ${Spacing.ZERO} ${Spacing.ZERO_5} ${Spacing.ZERO}`}>{title}</H3>
            ))}
        </div>
        {message && <BodyText type={BodyTypes.BODYP}>{message}</BodyText>}
        {children && <ChildMessages>{children}</ChildMessages>}
        <ActionWrapper>
          {onCancel && (
            <Action
              name={`${name ?? 'confirmation-modal'}-cancel-prompt`}
              actionStyle={ActionStyle.BUTTON}
              styleProps={{buttonStyle: ButtonStyle.TERTIARY, width: ButtonWidth.FULL_AT_MOBILE}}
              actionType={ActionType.ONCLICK}
              typeProps={{type: 'button', onClick: handleCancel}}>
              {!!cancelCaption ? cancelCaption : <FormattedMessage message={GLOBAL_TRANSLATIONS.CANCEL} />}
            </Action>
          )}
          {confirmationButton}
        </ActionWrapper>
      </PromptContent>
    </Modal>
  );
};

const ChildMessages = styled.div`
  margin-bottom: 27px;
`;

const PromptContent = styled.div`
  width: 500px;
  max-width: 100%;
`;

export default ConfirmationPrompt;
